import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as productApi from './api'
import type { ProductRequest, ProductUpdateRequest } from './types'

export const productKeys = {
  active: ['products', 'active'] as const,
  all: ['products', 'all'] as const,
  composition: (code: string) => ['configuration-composition', code] as const,
}

function invalidateDict(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: productKeys.active })
  qc.invalidateQueries({ queryKey: productKeys.all })
}

/** Active products (elements) - for composition picker, dropdowns. */
export function useProducts() {
  return useQuery({ queryKey: productKeys.active, queryFn: () => productApi.listProducts(true), staleTime: Infinity })
}

/** All products (incl. inactive) for the admin DictionaryPanel. */
export function useProductsAdmin() {
  return useQuery({ queryKey: productKeys.all, queryFn: () => productApi.listProducts(false) })
}

export function useCreateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: ProductRequest) => productApi.createProduct(payload),
    onSuccess: () => invalidateDict(qc),
  })
}

export function useUpdateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { code: string; payload: ProductUpdateRequest }) =>
      productApi.updateProduct(vars.code, vars.payload),
    onSuccess: () => invalidateDict(qc),
  })
}

export function useDeleteProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => productApi.deleteProduct(code),
    onSuccess: () => invalidateDict(qc),
  })
}

// ----- composition -----

export function useConfigurationComposition(configurationCode: string) {
  return useQuery({
    queryKey: productKeys.composition(configurationCode),
    queryFn: () => productApi.listComposition(configurationCode),
  })
}

export function useSetConfigurationComposition(configurationCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (productCodes: string[]) => productApi.setComposition(configurationCode, productCodes),
    onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.composition(configurationCode) }),
  })
}

export function useSetProductSections(productCode: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => productApi.setProductSections(productCode, ids),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin', 'forms', 'schema', `product/${productCode}`] }),
  })
}
